import React, { Component } from 'react';
import moment, { Moment } from 'moment';
import { Box } from 'bloomer/lib/elements/Box';
import { Columns } from 'bloomer/lib/grid/Columns';
import { Column } from 'bloomer/lib/grid/Column';
import { TimePicker } from 'antd';
import { Button } from 'bloomer/lib/elements/Button';
import Conditional from './Conditional';
import Spacer from '../elements/Spacer';
import styled from 'styled-components';
import api from '../util/api';
import { confirmSuccessAfter, infoAlert, confirmPopupBefore, popupErrorFromServer } from '../util/helperFunctions';

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

interface PropTypes {
  enrichment: Enrichment;
  aNumber: Ianimal['aNumber'];
  i: number;
  refresh: () => void;
}

interface StateTypes {
  editMode: boolean;
  newTimeReturned: Moment;
  newTimeTakenOut: Moment;
  isLoading: boolean;
  [Key: string]: any;
}

class EditableEnrichment extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    editMode: false,
    isLoading: false,
    newTimeReturned: moment(this.props.enrichment.timeReturned),
    newTimeTakenOut: moment(this.props.enrichment.timeTakenOut),
  };

  toggleEdit = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ editMode: !this.state.editMode });
  };

  handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { enrichment } = this.props;
    const { newTimeReturned, newTimeTakenOut } = this.state;

    if (!newTimeReturned || !newTimeTakenOut) {
      return infoAlert('You must select both a start and an end time.');
    }

    if (newTimeReturned.unix() <= newTimeTakenOut.unix()) {
      return infoAlert('The time returned must be after the time taken out.');
    }

    this.setState({
      isLoading: true,
    });

    confirmSuccessAfter('Time updated successfully', async () => {
      try {
        if (!enrichment._id) return;
        await api.enrichmentData.patch(enrichment._id, {
          ...enrichment,
          timeReturned: newTimeReturned.toISOString(),
          timeTakenOut: newTimeTakenOut.toISOString(),
        });

        this.setState({
          editMode: false,
          isLoading: false,
        });

        await this.props.refresh();
      } catch (err) {
        popupErrorFromServer(err);
      }
    });
  };

  handleDelete = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    const { enrichment } = this.props;
    this.setState({
      isLoading: true,
    });

    confirmPopupBefore(
      'Are you sure sure you want to delete this enrichment?',
      () => {
        confirmSuccessAfter('Enrichment deleted successfully!', async () => {
          try {
            if (!enrichment._id) return;
            await api.enrichmentData.delete(enrichment._id);

            this.setState({
              isLoading: false,
            });

            await this.props.refresh();
          } catch (err) {
            popupErrorFromServer(err);
          }
        });
      },
      () =>
        this.setState({
          isLoading: false,
        }),
    );
  };

  handleTimeChange = (str: 'newTimeReturned' | 'newTimeTakenOut') => (time: moment.Moment) =>
    this.setState({ [str]: time });

  render() {
    const { aNumber, enrichment, i } = this.props;
    const { editMode, newTimeReturned, newTimeTakenOut, isLoading } = this.state;
    const { timeReturned, enrichmentType, walkerName, timeTakenOut } = enrichment;

    return (
      <Box key={`${aNumber}-${moment(timeReturned).unix()}`}>
        <form onSubmit={this.handleSubmit}>
          <Columns isVCentered={true}>
            <Column isSize="narrow">{i + 1}. </Column>
            <Column isSize="1/4">{enrichmentType}</Column>
            <Column isSize="1/4">{walkerName}</Column>
            <Column isSize="1/4">
              <Conditional
                check={editMode}
                alt={
                  <>
                    {moment(timeTakenOut).format('h:mm A')} to {moment(timeReturned).format('h:mm A')}
                  </>
                }
              >
                <Flex>
                  <TimePicker
                    use12Hours={true}
                    format="h:mm a"
                    onChange={this.handleTimeChange('newTimeTakenOut')}
                    value={moment(newTimeTakenOut)}
                  />
                  <Spacer>to</Spacer>
                  <TimePicker
                    use12Hours={true}
                    format="h:mm a"
                    onChange={this.handleTimeChange('newTimeReturned')}
                    value={moment(newTimeReturned)}
                  />
                </Flex>
              </Conditional>
            </Column>
            <Column isSize="1/4">
              <Spacer>
                <Conditional
                  check={editMode}
                  alt={
                    <Button isColor="primary" onClick={this.toggleEdit} disabled={isLoading} isLoading={isLoading}>
                      Edit
                    </Button>
                  }
                >
                  <Button isColor="primary" type="submit" disabled={isLoading} isLoading={isLoading}>
                    Save
                  </Button>
                </Conditional>
              </Spacer>
              <Spacer>
                <Button
                  isColor="danger"
                  type="button"
                  disabled={isLoading}
                  isLoading={isLoading}
                  onClick={this.handleDelete}
                >
                  Delete
                </Button>
              </Spacer>
            </Column>
          </Columns>
        </form>
      </Box>
    );
  }
}

export default EditableEnrichment;
